/* eslint-disable no-unused-vars */
import { ECONTRACT_STATUS, EPARTY_STATUS } from "../../../../enums";
import { IApiResponse } from "../../models";
import { EContractActions } from "../contractActions/enum";
import { IUserInfoResponseData } from "../user/models";
import { IOrganizations } from "../../../types/Organisation";

export enum EContractType {
  MILESTONE_CONTRACT = "MILESTONE_CONTRACT",
  BROKERAGE_CONTRACT = "BROKERAGE_CONTRACT",
  COMPLEX_CONTRACT = "COMPLEX_CONTRACT",
  QUICK_TRANSACTION = "QUICK_TRANSACTION",
  VEHICLES = "VEHICLES",
  DEVICES = "DEVICES",
  FURNITURE = "FURNITURE",
  REAL_ESTATES = "REAL_ESTATES",
  SERVICES = "SERVICES",
  ANIMALS = "ANIMALS",
}
export enum ESenderRole {
  PROVIDER = "PROVIDER",
  CUSTOMER = "CUSTOMER",
  BROKER = "BROKER",
  ADMIN = "ADMIN",
  CLIENT_ADMIN = "CLIENT_ADMIN",
  SERVICE_PROVIDER = "SERVICE_PROVIDER",
  ADMIN_OPERATIONS = "ADMIN_OPERATIONS",
  ADMIN_SETTLEMENT = "ADMIN_SETTLEMENT",
}
export interface ItemDetailsDTO {
  title: string;
  description: string;
  field?: IFieldDTO[];
  images: string[];
  attachments: string[];
  category: string;
}
export interface IFieldDTO {
  key: string;
  value: string;
}
export enum ECurrency {
  SAR = "SAR",
}
export enum EShipmentType {
  SHIPMENT_COMPANY = "SHIPMENT_COMPANY",
  FACE_TO_FACE = "FACE_TO_FACE",
  VIRTUAL = "VIRTUAL",
  NONE = "NONE",
}
export interface IShipmentEvent {
  status: string;
  timestamp: string;
}

export interface IAddressWithTimestamp {
  street?: string;
  nationalAddressCode?: string;
  city?: string;
  district?: string;
  postalCode?: string;
  countryCode?: string;
  submittedAt?: string;
}

/** Address shape returned in `addressesMap` (keyed by userId) */
export interface IAddressDTO {
  id?: number;
  countryCode?: string;
  city?: string;
  postalCode?: string;
  district?: string;
  province?: string;
  street?: string;
  buildingNo?: string | null;
  verified?: boolean;
  cityCode?: number;
  districtCode?: number;
  provinceCode?: number;
  nationalAddress?: string;
}

export interface IFeeBreakdownStage {
  amount: number;
  actualShippingCost?: number;
  waffyProfit?: number;
}

export interface IFeeBreakdownSettled extends IFeeBreakdownStage {
  waffyPercentage?: number;
}

export interface IFeeBreakdown {
  estimated?: number | null;
  charged?: number | null;
  settled?: number | null;
  originalPrice?: number;
  settledOriginalPrice?: number;
  feeMargin?: number;
  settledReason?: string;
}

export interface IShipmentDTO {
  number?: string | null;
  provider?: string;
  /** The actual shipping company (e.g. "NAQEL") */
  shipmentCompany?: string;
  fee: number;
  feeBreakdown?: IFeeBreakdown | null;
  currency?: ECurrency | null;
  feePayee?: string;
  info?: string;
  type?: EShipmentType;
  courierPhoneNumber?: string;
  /** Legacy/mobile tracking code */
  trackingCode?: string;
  /** Booking ID from the shipment provider (e.g. Naqel booking number) */
  bookingId?: string;
  /** Direct tracking URL from the provider */
  trackingURL?: string | null;
  shipmentRequestId?: string;
  /** Waybill / sticker PDF URL */
  stickerUrl?: string | null;
  /** Legacy field name for waybill URL */
  waybillUrl?: string;
  /** Shipment status (e.g. SHIPMENT_REQUESTED) */
  status?: string;
  deliveryOptionName?: string;
  createdAt?: string;
  events?: IShipmentEvent[];
  isLocker?: boolean;
  lockerPaymentUrl?: string | null;
  isLockerShipmentPaid?: boolean;
  lockerCashoutGatewayPaymentId?: string | null;
  lockerCashoutAttachmentsViewUrl?: string | null;
  lockerCashoutRegisteredAt?: string | null;
}
export enum EReturnPolicy {
  RETURN_SPECIFICATIONS_VIOLATED = "RETURN_SPECIFICATIONS_VIOLATED",
  RETURN_WITHOUT_REASONS_DURING_EXAMINATION_PERIOD = "RETURN_WITHOUT_REASONS_DURING_EXAMINATION_PERIOD",
  NO_RETURN = "NO_RETURN",
}
export enum EFeePayee {
  PROVIDER = "PROVIDER",
  CUSTOMER = "CUSTOMER",
  BOTH = "BOTH",
}
export interface IReturnItemDTO {
  returnReason?: string | null;
  returnReasonDesc?: string | null;
  images?: string[] | null;
}

export type Escalations = {
  createdAt: Date;
  escalationType: EContractActions;
};

export enum TransactionStatus {
  COMPLETED = "COMPLETED",
  SUCCESS = "SUCCESS",
  PENDING = "PENDING",
  IN_PROGRESS = "IN_PROGRESS",
  FAILED = "FAILED",
  NOT_SENT = "NOT_SENT",
  INITIATED = "INITIATED",
}

export interface Transactions {
  createdAt: string;
  id: string;
  type: string;
  fromUserId?: string;
  referenceTransactionId: string;
  status: TransactionStatus;
  paymentMethod?: string;
  beneficiaryId: string;
  amount?: number;
}

export enum ContractClassification {
  STOCK_CONTRACT = "STOCK_CONTRACT",
  STOCK_CONTRACT_HARAJ = "STOCK_CONTRACT_HARAJ",
  BASIC = "BASIC",
  BASIC_HARAJ = "BASIC_HARAJ",
  BROKER_CONTRACT = "BROKER_CONTRACT",
}
export interface UserProfile {
  id: number;
  userId?: string;
  userIdentification?: number;
  phoneNumber: string;
  nationalIdVerified: true;
  role?: ESenderRole;
  amount?: number;
  feePercentage?: number;
  fee?: number;
  sudoName?: string;
  sudoRole?: string;
  status?: EPARTY_STATUS;
  arbitrator?: boolean;
  isSender?: boolean;
  /** Contract this party funded their share from, when they paid by transfer. */
  referenceContractId?: string;
  /** True when the party has at least one non-deleted bank account. */
  hasBankInfo?: boolean;
  /** True when the party has at least one non-deleted address. */
  hasAddressInfo?: boolean;
  profile: {
    firstName: string;
    lastName: string;
    fullName: string;
    languageCode: string;
    photoUrl: string;
    feedbackOverallRatingScore: number;
  };
}

export type UserParty = Partial<Omit<UserProfile, "id" | "userIdentification">>;

export type PartiesProfiles = {
  [key in ESenderRole]: Partial<UserProfile>[];
};

export interface CashOutAmount {
  id?: number;
  userIdentification?: string;
  amountDue?: number;
  feePercentage?: number;
  fee?: number;
  cashOutAmount: number;
  manualWithDrawCashOut?: boolean;
  settlementAmountStatus: string;
  multiCashOut?: boolean;
  settlementRequestId?: string;
}

export enum FeeCalculationType {
  FLAT = "FLAT",
  PERCENTAGE = "PERCENTAGE",
}

export enum AddOnFeeCategory {
  SHIPPING_FEES = "SHIPPING_FEES",
  SERVICE_FEES = "SERVICE_FEES",
  EXTRA_AMOUNT = "EXTRA_AMOUNT",
}

export interface AddOnFees {
  amount: number;
  payee: string;
  feeType: FeeCalculationType;
  type: AddOnFeeCategory;
}

export interface OperationsMetaData {
  cashIn?: string;
  category?: string;
  operationStatus?: string;
  operationalType?: string;
  escalation?: string;
  operationResult?: string;
  commisionBearer?: string;
  shippingCompany?: string;
  cashOut?: string;
  cashOutOutSystem?: string;
  cashOutOutType?: string;
  organization?: string;
  organizationWaffyFee?: string;
  organizationOperationResult?: string;
  comments?: string;
}

export interface IWaffyExtraFees {
  amount: number;
  description: string | null;
  type: AddOnFeeCategory;
}

export interface ISuggestedCashOutAmount {
  _id: number;
  userIdentification: string;
  amountDue: string;
  feePercentage: number;
  fee: string;
  cashOutAmount: string;
  manualWithDrawCashOut: boolean;
  multiCashOut: boolean;
}

export interface IShippingContact {
  name?: string;
  mobile?: string;
}

export interface IContractDetailsResponseData {
  cashOutAmountList?: CashOutAmount[];
  suggestedCashOutAmountList?: ISuggestedCashOutAmount[];
  addonFeesList?: AddOnFees[];
  activeChildrenCount?: number;
  organizationCode?: string;
  operationsMetaData?: OperationsMetaData;
  activeChildrenTotalAmount?: number;
  activeChildrenPartiesCount?: number;
  contractDefinitionId?: string;
  brokerFee?: number;
  id: string;
  type: EContractType;
  currentStep?: string;
  contractClassification: ContractClassification;
  isAcceptable?: boolean;
  isInspectable?: boolean;

  parentContractId?: string;
  milestones?: IContractDetailsResponseData[];

  shortId: string;
  originalItemPrice: number;
  paymentMethods?: Partial<Record<EPaymentType, EPaymentBrand[]>>;

  senderId: string;
  brokerId: string;
  senderPhoneNumber: string;
  senderRole: ESenderRole;
  isParent?: boolean;
  parentId?: string;

  itemDetail: ItemDetailsDTO;
  itemPrice: number;
  settledAt?: string;
  unitPrice?: number;
  fee: number;
  feePercentage: number;
  totalAmount: number;
  currency: ECurrency;
  feePayee: EFeePayee;
  deadLine?: string;
  cancellationConditions?: string;
  acceptanceConditions?: string;
  hasVoucher?: boolean;
  waffyExtraFees: IWaffyExtraFees[];
  address?: {
    city: string;
    countryCode: string;
    district: string;
    id: number;
    postalCode: string;
    street: string;
  };
  metaData: {
    [key: string]: string | number | boolean | IShippingContact | undefined;
    sellerRequiresShippingInfo: boolean;
    buyerShippingContact?: IShippingContact;
    sellerShippingContact?: IShippingContact;
  };

  shipment?: IShipmentDTO | null;
  /** Legacy pickup/delivery map (may not be present in all responses) */
  addressMap?: {
    pickup?: IAddressWithTimestamp;
    delivery?: IAddressWithTimestamp;
  };
  /** Address map keyed by userId — use senderId for pickup, receiverId for delivery */
  addressesMap?: Record<string, IAddressDTO>;
  deliveryInDays: number;
  partiesProfiles?: PartiesProfiles;
  parties?: UserParty[];
  returnPolicy: EReturnPolicy;
  inspectionPeriod: number;
  returnFee: number;
  returnFeePayee?: EFeePayee | null;
  transactions: Transactions[];
  receiverId: string;
  receiverPhoneNumber?: string | null;
  receiverRole?: ESenderRole;

  ReturnItemDTO?: IReturnItemDTO | null;
  returnShipment?: IShipmentDTO | null;

  waffyTermsAccepted: boolean;

  status: ECONTRACT_STATUS;
  createdAt: Date | string;

  escalations?: IContractEscalationDetails[];
  organization?: Partial<IOrganizations> | null;
  originalQuantity?: number;
  quantity?: number;

  provider: IUserInfoResponseData;
  customer?: IUserInfoResponseData;

  customerAdditionalInfo: string;
  customerShippingAddress: string;

  shippable?: boolean;
  uncompletedActions?: IContractUncompletedAction[];
  settlementRequests?: ISettlementRequest[];
  sourceContractId?: string;
  settlementAdvisory?: ISettlementAdvisory;
  buyRequestId?: string;
}

export enum ESettlementAdvisoryOutcome {
  AUTO_ALLOW = "AUTO_ALLOW",
  REVIEW = "REVIEW",
  BLOCK = "BLOCK",
}

export interface ISettlementAdvisoryContract {
  status: ECONTRACT_STATUS;
  isSettled: boolean;
  escalationsOpen: boolean;
  totalAmount: number;
  category: string;
  classification: string;
  partiesCount: number;
  hasArbitrator: boolean;
  paymentConfirmed: boolean;
}

export interface ISettlementAdvisoryParty {
  userId: string;
  role: ESenderRole;
  kycLevel: string;
  userStatus: string;
  manualWithDrawCashOut: boolean;
  credited: boolean;
}

export interface ISettlementAdvisoryRisk {
  fraudScore: number;
  fraudDecision: string;
  anomalyFlags: string[];
  blacklistHit: boolean;
  disputeVelocity: number;
  signalRefs: string[];
}

export interface ISettlementAdvisoryOrg {
  organizationCode: string;
  optedIn: boolean;
  trustTier: string;
  dailyCapRemaining: number;
}

export interface ISettlementAdvisoryExposure {
  rollingAmount: number;
  rollingCount: number;
}

export interface ISettlementAdvisoryContextBundle {
  contractId: string;
  contract: ISettlementAdvisoryContract;
  parties: ISettlementAdvisoryParty[];
  risk: ISettlementAdvisoryRisk;
  org: ISettlementAdvisoryOrg;
  exposure: ISettlementAdvisoryExposure;
}

export interface ISettlementAdvisoryDecisionReason {
  ruleId: string;
  message: string;
  effect: string;
}

export interface ISettlementAdvisoryDecision {
  decisionId: string;
  rulesetVersion: string;
  outcome: ESettlementAdvisoryOutcome;
  reasons: ISettlementAdvisoryDecisionReason[];
  evaluatedAt: string;
}

export interface ISettlementAdvisory {
  contextBundle: ISettlementAdvisoryContextBundle;
  decision: ISettlementAdvisoryDecision;
}

export interface ISettlementRequest {
  id: string;
  destinationContractId: string;
  destinationContractShortId: string;
  amount: number;
  status: string;
  createdByActorId: string;
  createdByActorRole: ESenderRole | null;
  createdAt: string;
  decidedByActorId: string | null;
  decidedByActorRole: ESenderRole | null;
  decidedAt: string | null;
  executionTransactionId: string | null;
  rejectionReason: string | null;
}

export interface IEscalationSupportingDocument {
  url: string | null;
  bucketName?: string | null;
  objectName?: string | null;
  title?: string | null;
  description?: string | null;
  public?: boolean;
}

export interface IContractEscalationDetails {
  ticketId: string;
  reason: string;
  message: string;
  userId: string;
  userRole: ESenderRole;
  escalationType: EEscalaionType;
  resolved: boolean;
  createdAt: string;
  resolvedAt: string | null;
  action?: string | null;
  status?: EEscalationStatus | null;
  proposedSettlement?: IEscalationProposedSettlement | null;
  supportingDocuments?: IEscalationSupportingDocument[] | null;
  proposedSettlementUpdatedBy?: string | null;
  proposedSettlementUpdatedAt?: string | null;
  rejectionComment?: string | null;
}

export enum EEscalationStatus {
  OPEN = "OPEN",
  RESOLVED_CUSTOMER = "RESOLVED_CUSTOMER",
  RESOLVED_PROVIDER = "RESOLVED_PROVIDER",
  PENDING_SETTLEMENT = "PENDING_SETTLEMENT",
  SETTLED = "SETTLED",
}

export interface IPartyCashOut {
  id: string | number;
  amountDue: number;
  fee?: number;
  feePercentage?: number;
  cashOutAmount?: number;
  userIdentification?: string | number;
  settlementAmountStatus?: string | null;
}

/** Escalation detail from contract API: settlement rows plus optional proposer id */
export interface IProposedEscalationSettlementDetails {
  parties: IPartyCashOut[];
  userId?: string;
}

/** Request DTOs use a flat array; contract `escalations[].proposedSettlement` may be nested */
export type IEscalationProposedSettlement =
  | IPartyCashOut[]
  | IProposedEscalationSettlementDetails;

export interface IContractUncompletedAction {
  actorId: string;
  action: string;
  createdAt: string;
  actorRole?: ESenderRole | string;
}
export enum EEscalaionType {
  CUSTOMER_DENY_DELIVERY = "CUSTOMER_DENY_DELIVERY",
  PROVIDER_ESCALATE_DELIVERY = "PROVIDER_ESCALATE_DELIVERY",
  CUSTOMER_REJECT_ITEM = "CUSTOMER_REJECT_ITEM",
  PROVIDER_ESCALATE_ITEM = "PROVIDER_ESCALATE_ITEM",
  CUSTOMER_CHECK_ESCALATION = "CUSTOMER_CHECK_ESCALATION",
  PROVIDER_CHECK_ESCALATION = "PROVIDER_CHECK_ESCALATION",
}

export interface IMilestonesPartiesData {
  [key: string]: {
    phoneNumber: string | number;
    role: ESenderRole;
    sudoRole?: string;
    amount: string | number;
  }[];
}

export enum EPaymentType {
  HYPER_PAY = "HYPER_PAY",
  MANUAL_BANK_TRANSFER = "MANUAL_BANK_TRANSFER",
  MOYASAR = "MOYASAR",
  TABBY = "TABBY",
  APPLE_PAY = "APPLE_PAY",
  STCPAY = "STCPAY",
  NEO = "NEO",
  TAMARA = "TAMARA",
}

export enum EPaymentBrand {
  MADA = "MADA",
  VISA = "VISA",
  MASTER = "MASTER",
  AMEX = "AMEX",
  APPLE_PAY = "APPLE_PAY",
  STCPAY = "STCPAY",
  TABBY = "TABBY",
  BANK_TRANSFER = "BANK_TRANSFER",
  NEO = "NEO",
  TAMARA = "TAMARA",
}

export interface IContractDetailsBody {
  type: EContractType;
  senderRole: ESenderRole;
  draft?: boolean;
  itemDetail: Partial<ItemDetailsDTO>;
  itemPrice: number | string;
  currentStep?: string;
  currency: ECurrency;
  feePayee?: EFeePayee;
  shipment: {
    type: EShipmentType;
    fee?: number;
    currency: ECurrency;
  };
  paymentMethods?: Partial<Record<EPaymentType, EPaymentBrand[]>>;
  brokerFee?: number | string;
  returnPolicy: EReturnPolicy;
  inspectionPeriod?: number;
  isInspectable?: boolean;
  isAcceptable?: boolean;
  deliveryInDays?: number;
  returnFee?: number;
  deadLine?: string;
  cancellationConditions?: string;
  acceptanceConditions?: string;
  returnFeePayee?: EFeePayee | null;
  receiverId: string | null;
  receiverName: string | null;
  returnItem?: IReturnItemDTO;
  contractClassification: ContractClassification;
  returnShipment?: {
    type: EShipmentType;
    fee: number;
    currency: ECurrency;
  };
  waffyTermsAccepted: boolean;
  interactiveContract?: boolean;
  quantity?: number;
  clientReferenceId?: string;
}

export interface ITrasnactionObject {
  type: ETrasactionsType;
  id: string;
  createdAt: string;
  referenceTransactionId: string;
}
export enum ETrasactionsType {
  CUSTOMER_PAY = "CUSTOMER_PAY",
  REFUND_BUYER = "REFUND_BUYER",
  CLOSE_RELEASE_TO_PROVIDER = "CLOSE_RELEASE_TO_PROVIDER",
  RELEASE_TO_PROVIDER = "RELEASE_TO_PROVIDER",
  RELEASE_TO_SELLER = "RELEASE_TO_SELLER",
  CLOSE_RELEASE_TO_SELLER = "CLOSE_RELEASE_TO_SELLER",
  MANUAL_BANK_TRANSFER = "MANUAL_BANK_TRANSFER",
  CASH_OUT = "CASH_OUT",
}
export interface IContractDetailsResponse extends IApiResponse {
  data: IContractDetailsResponseData;
}

export interface IContractActionsResponse extends IApiResponse {
  data: IContractActionsResponseData[];
}

export interface IUpdateContractRequestBody {
  title: string;
  description: string;
  fields?: {
    key: string;
    value: string;
  }[];
  images?: string[];
}

export interface IContractListResponse extends IApiResponse {
  data: IContractDetailsResponseData;
}
export interface IContractDetailsResponseData {
  content: IContractDetailsResponseData[];
  empty: boolean;
  first: boolean;
  last: boolean;
  number: number;
  numberOfElements: number;
  totalElements: number;
  totalPages: number;
}

export interface IContractActionsResponseData {
  action: string;
  actionStatus: string;
  actorId: string;
  actorRole: ESenderRole;
  createdAt: Date;
  to: string;
}
export interface IPostFeedBackRequest {
  contractId: string;
  shortContractId: string;
  contractTitle: string;
  receiverId?: string;
  contractCustomerId?: string;
  providerId?: string;
  providerRole: string;
  ratingScore: number;
  comment?: string;
}

export interface DiscountInfo {
  amountLimit: number;
  percentOff: number;
}
export interface voucherContent {
  amount: number;
  discountAmount: number;
  domainId: string;
  domainType: string;
  externalRedemptionId: string;
  id: number;
  status: string;
  totalAmount: number;
  userId: string;
  voucherCode: string;
  discountInfo: DiscountInfo;
}

export interface IVoucherResponseData {
  content: voucherContent[];
}

export interface IContractDetailsResponse extends IApiResponse {
  data: IContractDetailsResponseData;
}

export interface IContractActionsResponse extends IApiResponse {
  data: IContractActionsResponseData[];
}

export interface IVoucherResponse extends IApiResponse {
  data: IVoucherResponseData;
}

export interface IContractStatsResponse extends IApiResponse {
  data: IContractStats;
}

export interface IContractStats {
  activeUsersStats: {
    registered: number;
    unRegistered: number;
  };
  paidUsersStats: {
    paid: number;
    unPaid: number;
  };
  paymentStats: {
    amountPaid: number;
    amountUnpaid: number;
  };
}

// CAS.E0001 -> Sum of Amounts for PROVIDERS is not same as ItemPrice for the contract with Id %s
// CAS.E0002 -> Sum of Amounts for CUSTOMERS is greater than ItemPrice for the contract with Id %s
// CAS.E0003 -> Sum of milestones item prices: %d is larger than the unallocated item price of parent contract %d
// CAS.E0004 -> isSender not exists
// CAS.E0005 -> Contract with Id %s has less than two parties.
// CAS.E0006 -> Contract with Id %s has more/less than one sender.
export const PUBLISH_ERROR_CODES = {
  "CAS.E0001": "lbl_sum_providers_error",
  "CAS.E0002": "lbl_sum_customers_error",
  "CAS.E0003": "lbl_sum_milestones_price_error",
  "CAS.E0004": "lbl_no_sender_error",
  "CAS.E0005": "lbl_parties_error",
  "CAS.E0006": "lbl_sender_count_error",
};

export const ERROR_PATTERNS = {
  "CAS.E0001": /contract with Id (\w+)/,
  "CAS.E0002": /contract with Id (\w+)/,
  "CAS.E0003": /item prices: (\d+).*contract (\d+)/,
  "CAS.E0004": /isSender not exists/,
  "CAS.E0005": /Contract with Id (\w+) has less than two parties/,
  "CAS.E0006": /Contract with Id (\w+) has more\/less than one sender/,
};
